import { EARTHQUAKES } from 'slices/mapSlice';
import {
  EarthquakeFeature,
  MapMemo,
  LayerClickHandler,
  MapboxLayerData,
} from './mapEventsEffectTypes';
import {
  checkLayerExistsMemo,
  checkSourceExistsMemo,
} from './mapEventsEffectMemos';

export const earthquakes_source_layer = 'earthquakes_source_layer';
export const earthquakes_clusters_layer_id = 'earthquakes_clusters_layer_id';
export const earthquakes_cluster_count_layer_id =
  'earthquakes_cluster_count_layer_id';
export const earthquakes_unclustered_point_layer_id =
  'earthquakes_unclustered_point_layer_id';

export function addEarthquakeLayers({
  checkLayerToggle,
  earthquakesData,
  map,
  setLayerClicked,
}: {
  checkLayerToggle: (layerName: string) => boolean;
  earthquakesData: EarthquakeFeature[];
  map: MapMemo;
  setLayerClicked: LayerClickHandler;
}) {
  if (checkLayerToggle(EARTHQUAKES)) {
    const checkSourceExists = checkSourceExistsMemo(map);

    if (!checkSourceExists(earthquakes_source_layer)) {
      map.current.addSource(earthquakes_source_layer, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: earthquakesData || [],
        },
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50,
      });
    }

    const earthquakesClustersLayerConfig = {
      id: earthquakes_clusters_layer_id,
      source: earthquakes_source_layer,
      type: 'circle',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'point_count'],
          '#FFA500',
          20,
          '#FFA500',
          50,
          '#FFA500',
        ],
        'circle-radius': ['step', ['get', 'point_count'], 20, 20, 30, 50, 40],
        'circle-opacity': 0.5,
      },
      metadata: {
        eventlayer: true,
        usermade: undefined,
      },
    };

    const earthquakesClusterCounterLayerConfig = {
      id: earthquakes_cluster_count_layer_id,
      source: earthquakes_source_layer,
      type: 'symbol',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': ['get', 'point_count_abbreviated'],
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12,
      },
      metadata: {
        eventlayer: true,
        usermade: undefined,
      },
    };

    const earthquakesUnclusteredPointLayerConfig = {
      id: earthquakes_unclustered_point_layer_id,
      source: earthquakes_source_layer,
      type: 'circle',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': '#FFA500',
        'circle-radius': 4,
        'circle-stroke-width': 1,
        'circle-stroke-color': '#FFA500',
        'circle-opacity': 0.5,
      },
      generateId: true,
      metadata: {
        eventlayer: true,
        usermade: undefined,
      },
    };

    const checkLayerExists = checkLayerExistsMemo(map);

    if (!checkLayerExists(earthquakes_clusters_layer_id)) {
      map.current.addLayer(earthquakesClustersLayerConfig);
    }

    if (!checkLayerExists(earthquakes_cluster_count_layer_id)) {
      map.current.addLayer(earthquakesClusterCounterLayerConfig);
    }

    if (!checkLayerExists(earthquakes_unclustered_point_layer_id)) {
      map.current.addLayer(earthquakesUnclusteredPointLayerConfig);
    }

    map.current.on('click', earthquakesClustersLayerConfig.id, (e) => {
      const features: EarthquakeFeature[] = <EarthquakeFeature[]>(
        map.current.queryRenderedFeatures(e.point, {
          layers: [earthquakesClustersLayerConfig.id],
        })
      );
      const clusterId = features[0].properties.cluster_id;
      map.current
        .getSource(earthquakes_source_layer)
        .getClusterExpansionZoom(clusterId, (err: any, zoom: any) => {
          if (err) return;

          map.current.easeTo({
            center: features[0].geometry.coordinates,
            zoom: zoom,
          });
        });
    });

    map.current.on(
      'click',
      earthquakesUnclusteredPointLayerConfig.id,
      (e: MapboxLayerData) => {
        const features = Array.from(
          new Set(e.features.map((feature: any) => JSON.stringify(feature)))
        ).map((strFeature) => JSON.parse(strFeature as string));
        setLayerClicked({
          layer: earthquakesUnclusteredPointLayerConfig,
          e: e,
          features: features,
        });
      }
    );

    map.current.on('mouseenter', earthquakesClustersLayerConfig.id, () => {
      map.current.getCanvas().style.cursor = 'pointer';
    });

    map.current.on('mouseleave', earthquakesClustersLayerConfig.id, () => {
      map.current.getCanvas().style.cursor = 'grab';
    });

    map.current.on(
      'mouseenter',
      earthquakesUnclusteredPointLayerConfig.id,
      () => {
        map.current.getCanvas().style.cursor = 'pointer';
      }
    );

    map.current.on(
      'mouseleave',
      earthquakesUnclusteredPointLayerConfig.id,
      () => {
        map.current.getCanvas().style.cursor = 'grab';
      }
    );
  }
}
export function updateEarthquakeFilters(
  checkLayerToggle: (layerName: string) => boolean,
  setFilter: (layerId: UUID, filter: any[]) => void
) {
  const earthquakesAreOn = checkLayerToggle(EARTHQUAKES);

  const earthquakesClusterFilter = earthquakesAreOn
    ? ['has', 'point_count']
    : ['has', 'point_count1'];
  setFilter(earthquakes_clusters_layer_id, earthquakesClusterFilter);
  setFilter(earthquakes_cluster_count_layer_id, earthquakesClusterFilter);
  setFilter(
    earthquakes_unclustered_point_layer_id,
    earthquakesAreOn ? ['!', ['has', 'point_count']] : ['has', 'point_count1']
  );
}

export function removeEarthquakeLayers({
  removeLayer,
  removeSource,
}: {
  removeLayer: (layerId: string) => void;
  removeSource: (sourceId: string) => void;
}) {
  removeLayer(earthquakes_clusters_layer_id);
  removeLayer(earthquakes_cluster_count_layer_id);
  removeLayer(earthquakes_unclustered_point_layer_id);

  removeSource(earthquakes_source_layer);
}

export function setEarthquakeLayersVisibility({
  setLayerVisibility,
  isVisible,
}: {
  setLayerVisibility: (layerId: string, isVisible: boolean) => void;
  isVisible: boolean;
}) {
  setLayerVisibility(earthquakes_clusters_layer_id, isVisible);
  setLayerVisibility(earthquakes_cluster_count_layer_id, isVisible);
  setLayerVisibility(earthquakes_unclustered_point_layer_id, isVisible);
}
